import { useState } from "react";
import { toast } from "sonner";
import { X, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";

const CATS = [
  { key: "communication", label: "Communication" },
  { key: "thoroughness", label: "Thoroughness" },
  { key: "constructive_feedback", label: "Constructive feedback" },
  { key: "professionalism", label: "Professionalism" },
  { key: "would_hire_again", label: "Would hire / work again" },
];

export function ReviewDialog({ jobId, subjectId, onClose, onDone }) {
  const [ratings, setRatings] = useState({
    communication: 5, thoroughness: 5, constructive_feedback: 5, professionalism: 5, would_hire_again: 5,
  });
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/reviews", { job_id: jobId, subject_id: subjectId, ratings, body });
      toast.success("Review submitted");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    // Overlay: scrollable backdrop so even at extreme zoom (200% / 400%)
    // or on tiny viewports the modal + its surrounding padding is reachable.
    // overflow-y-auto on the backdrop handles the degenerate case where
    // the modal itself can't shrink any further (very tall ratings column
    // at 400% zoom on a short screen).
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-dialog-title"
      data-testid="review-dialog-overlay"
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        {/*
          flex column + max-h-[calc(100dvh-2rem)] turns the form into a
          three-region layout:
            1. header  – flex-shrink-0, always visible at the top
            2. body    – flex-1 min-h-0 overflow-y-auto, scrolls ratings
                         + textarea when zoom pushes them past the viewport
            3. footer  – flex-shrink-0, Cancel + Submit are ALWAYS reachable
                         without having to zoom out or scroll the page
          max-h uses `100dvh` (dynamic viewport height) so mobile browser
          chrome collapsing doesn't suddenly reveal/hide the footer.
          On very short viewports (<=300px, e.g. 400% zoom) the whole form
          scrolls in the overlay and the footer stays sticky at the bottom.
        */}
        <form
          onSubmit={submit}
          className="w-full max-w-xl flex flex-col max-h-[calc(100dvh-2rem)] [@media(max-height:300px)]:max-h-none rounded-2xl bg-card shadow-2xl border"
        >
          <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b rounded-t-2xl bg-card flex-shrink-0">
            <h3 id="review-dialog-title" className="font-serif text-2xl">Leave a review</h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close review form"
              className="rounded-full p-2 hover:bg-muted"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto [@media(max-height:300px)]:overflow-visible px-6 py-5 space-y-5" data-testid="review-dialog-body">
            {CATS.map((c) => (
              <div key={c.key}>
                <div className="text-sm font-medium mb-1.5">{c.label}</div>
                <StarPicker value={ratings[c.key]} onChange={(v) => setRatings((r) => ({ ...r, [c.key]: v }))} />
              </div>
            ))}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Written review</label>
              <textarea rows={5} required minLength={10} className="sb-input"
                value={body} onChange={(e) => setBody(e.target.value)}
                placeholder="What worked, what stood out, what could improve…"
                data-testid={TID.reviews.body} />
            </div>
          </div>

          <div className="sticky bottom-0 z-10 flex flex-wrap justify-end gap-2 px-6 py-4 [@media(max-height:300px)]:py-2 border-t rounded-b-2xl bg-card flex-shrink-0" data-testid="review-dialog-footer">
            <button type="button" onClick={onClose} className="btn-ghost border" data-testid="review-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid={TID.reviews.submit}>
              {saving ? "Submitting…" : "Submit review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`p-1 transition-colors ${n <= value ? "text-[hsl(var(--sb-clay))]" : "text-muted-foreground/40 hover:text-[hsl(var(--sb-clay))]"}`}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}>
          <Star size={22} className={n <= value ? "fill-current" : ""} />
        </button>
      ))}
      <span className="ml-2 text-xs text-muted-foreground">{value} / 5</span>
    </div>
  );
}
